<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import {
  frontendURL,
  conversationUrl,
  conversationListPageURL,
} from 'dashboard/helper/URLHelper';
import { UNASSIGNED_QUEUE_SLUG } from 'dashboard/routes/dashboard/conversation/chatQueues';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import Logo from 'dashboard/components-next/icon/Logo.vue';

// The idle state an agent sees between conversations: instead of an ornament
// and a sentence, the two things worth doing right now - pick up the oldest
// unanswered conversation, or ask Tola AI what the queue looks like.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isEnterprise } = useConfig();
const { uiSettings, updateUISettings } = useUISettings();

const allConversations = useMapGetter('getAllConversations');
const conversationStats = useMapGetter('conversationStats/getStats');
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const waitingCount = computed(() => conversationStats.value.unAssignedCount);

// The oldest unassigned conversation still waiting on a reply, out of the ones
// the list already holds. When the loaded page has none, the card falls back to
// the Unassigned queue rather than asking the server for a different answer.
const oldestWaitingConversation = computed(() => {
  const waiting = allConversations.value.filter(
    conversation =>
      conversation.status === 'open' &&
      !conversation.meta?.assignee &&
      conversation.waiting_since
  );
  if (!waiting.length) return null;
  return waiting.reduce((oldest, conversation) =>
    conversation.waiting_since < oldest.waiting_since ? conversation : oldest
  );
});

const isCopilotAvailable = computed(
  () =>
    isEnterprise &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CAPTAIN
    )
);

const openOldestWaiting = () => {
  const accountId = route.params.accountId;
  const conversation = oldestWaitingConversation.value;
  const path = conversation
    ? frontendURL(conversationUrl({ accountId, id: conversation.id }))
    : conversationListPageURL({
        accountId,
        queueSlug: UNASSIGNED_QUEUE_SLUG,
      });
  router.push({ path });
};

const openCopilot = () => {
  updateUISettings({
    is_copilot_panel_open: true,
    is_contact_sidebar_open: false,
  });
};

const shortcuts = computed(() => [
  { keys: ['⌘', 'K'], description: t('CONVERSATION.EMPTY_STATE.CMD_BAR') },
  { keys: ['L'], description: t('CONVERSATION.IDLE_STATE.SHORTCUTS.LAYOUT') },
]);

const cardClass =
  'flex items-center w-full gap-3 px-3 py-2.5 text-left transition-colors border rounded-lg border-n-weak bg-n-solid-2 hover:bg-n-solid-3 disabled:opacity-60 disabled:hover:bg-n-solid-2';
const hotkeyClass =
  'h-5 px-1.5 text-xs font-medium text-n-slate-12 outline outline-n-container outline-1 bg-n-alpha-3';
</script>

<template>
  <div class="flex flex-col items-center justify-center w-full h-full px-6">
    <div class="flex flex-col w-full max-w-sm gap-3">
      <div class="flex flex-col items-center gap-2 text-n-slate-11">
        <Logo
          class="size-10 rounded-xl motion-safe:animate-onboarding-float"
          aria-hidden="true"
        />
        <h2 class="text-sm font-medium">
          {{ t('CONVERSATION.IDLE_STATE.TITLE') }}
        </h2>
      </div>

      <button
        v-if="waitingCount"
        type="button"
        data-testid="idle-state-open-oldest"
        :class="cardClass"
        @click="openOldestWaiting"
      >
        <span
          class="grid rounded-md size-8 shrink-0 place-items-center bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-clock size-4" aria-hidden="true" />
        </span>
        <span class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t('CONVERSATION.IDLE_STATE.WAITING.LABEL', {
                count: waitingCount,
              })
            }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.IDLE_STATE.WAITING.DESCRIPTION') }}
          </span>
        </span>
        <span
          class="text-xs font-medium ltr:ml-auto rtl:mr-auto shrink-0 text-n-brand"
        >
          {{ t('CONVERSATION.IDLE_STATE.WAITING.ACTION') }}
        </span>
      </button>

      <button
        v-if="isCopilotAvailable"
        type="button"
        data-testid="idle-state-open-copilot"
        :class="cardClass"
        :disabled="uiSettings.is_copilot_panel_open"
        @click="openCopilot"
      >
        <span
          class="grid rounded-md size-8 shrink-0 place-items-center bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-sparkles size-4" aria-hidden="true" />
        </span>
        <span class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.IDLE_STATE.COPILOT.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.IDLE_STATE.COPILOT.DESCRIPTION') }}
          </span>
        </span>
        <span
          class="text-xs font-medium ltr:ml-auto rtl:mr-auto shrink-0 text-n-brand"
        >
          {{ t('CONVERSATION.IDLE_STATE.COPILOT.ACTION') }}
        </span>
      </button>

      <div
        class="flex flex-wrap items-center justify-center mt-1 gap-x-4 gap-y-2"
      >
        <div
          v-for="shortcut in shortcuts"
          :key="shortcut.description"
          class="flex items-center gap-1.5"
        >
          <Hotkey
            v-for="key in shortcut.keys"
            :key="key"
            :custom-class="hotkeyClass"
          >
            {{ key }}
          </Hotkey>
          <span class="text-xs text-n-slate-11">
            {{ shortcut.description }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
